import type { SlateGameStatus, SlateStatus } from '../constants';

/**
 * Lock and reveal rules.
 *
 * This module is pure and takes `serverNow` as an argument — it never reads the clock
 * itself. The API calls it with Postgres `now()` from inside the same transaction as the
 * write, which is the only value that decides whether a pick is accepted. The web app
 * calls it with a server-anchored estimate purely so the UI can *display* the right state
 * and count down; a client that lies to itself still gets a 409 from the server.
 */

export type LockReason =
  | 'unlocked'
  | 'kickoff_passed'
  | 'slate_not_published'
  | 'game_voided'
  | 'game_canceled'
  | 'commissioner_locked';

export interface LockInput {
  serverNow: Date;
  contestKickoffAt: Date;
  slateStatus: SlateStatus;
  gameStatus: SlateGameStatus;
  /** `true` force-locks the week, `false` force-unlocks it, `null` is automatic. */
  picksLockedOverride: boolean | null;
}

export interface LockState {
  locked: boolean;
  reason: LockReason;
  /** Milliseconds until kickoff. Negative once kickoff has passed. */
  msUntilKickoff: number;
}

export function computeLockState(input: LockInput): LockState {
  const msUntilKickoff = input.contestKickoffAt.getTime() - input.serverNow.getTime();
  const state = (locked: boolean, reason: LockReason): LockState => ({
    locked,
    reason,
    msUntilKickoff,
  });

  // A commissioner override beats everything, in both directions. This is the escape
  // hatch for "the whole slate needs to freeze right now" and for "I need to let someone
  // fix a pick after a kickoff time was entered wrong".
  if (input.picksLockedOverride === true) return state(true, 'commissioner_locked');
  if (input.picksLockedOverride === false) return state(false, 'unlocked');

  // A voided or canceled game accepts no further picks, but the picks already on it are
  // kept and scored as void — they are never deleted.
  if (input.gameStatus === 'voided') return state(true, 'game_voided');
  if (input.gameStatus === 'canceled') return state(true, 'game_canceled');

  // Draft slates are commissioner-only. Completed weeks are closed.
  if (input.slateStatus !== 'published' && input.slateStatus !== 'in_progress') {
    return state(true, 'slate_not_published');
  }

  if (msUntilKickoff <= 0) return state(true, 'kickoff_passed');

  return state(false, 'unlocked');
}

/**
 * Whether other players' picks on this game may be shown.
 *
 * `at_kickoff` (the default) reveals a game's picks the moment that game locks, so a
 * player watching the early window can see the field without being able to copy anyone.
 * `at_slate_lock` holds everything back until the last game of the week kicks off.
 */
export function isRevealed(args: {
  lock: LockState;
  revealPolicy: 'at_kickoff' | 'at_slate_lock';
  /** Required for `at_slate_lock`: has every game in the week locked? */
  allGamesLocked?: boolean;
}): boolean {
  if (args.revealPolicy === 'at_slate_lock') {
    return args.allGamesLocked === true;
  }
  return args.lock.locked;
}

/** Compact countdown for the UI: `2d 4h`, `3h 12m`, `14m`, `Locked`. */
export function formatCountdown(msUntilKickoff: number): string {
  if (msUntilKickoff <= 0) return 'Locked';
  const totalMinutes = Math.floor(msUntilKickoff / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Tracks the offset between this device's clock and the server's, so countdowns stay
 * honest on a phone whose clock is wrong. Every API response carries `serverTime`; the
 * client feeds it in here and reads `now()` from this instead of `new Date()`.
 *
 * This is for display only. Enforcement is always the server's `now()`.
 */
export class ServerClock {
  private offsetMs = 0;

  sync(serverTimeIso: string): void {
    const serverMs = Date.parse(serverTimeIso);
    if (Number.isFinite(serverMs)) {
      this.offsetMs = serverMs - Date.now();
    }
  }

  now(): Date {
    return new Date(Date.now() + this.offsetMs);
  }

  get offset(): number {
    return this.offsetMs;
  }
}
